import { CACHING_TIME } from '@/store/utils';
import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import {
  BatchDCUDetailsResponse,
  BatchDCUViewResponse,
  UploadFirmwareFileResponse
} from '../../types';
import {
  UpgradeDCUCommandResponse,
  UpgradeDCUFWCommandPayload,
  UploadFirmwareFilePayload
} from '../../types/records/dcu-fw-upgrade';

export const dcuFirmwareUpgradeEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'dcuFWApi'
  >
) => ({
  getDCUBatchList: builder.query<BatchDCUViewResponse, void>({
    query: () => ({
      url: `batches`,
      method: 'GET'
    }),
    providesTags: [],
    keepUnusedDataFor: CACHING_TIME
  }),
  fetchDCUBatchDetails: builder.query<
    BatchDCUDetailsResponse,
    { batch_id: string }
  >({
    query: ({ batch_id }) => ({
      url: `batches/${batch_id}`
    })
  }),
  executeDCUFWUpgradeCommand: builder.mutation<
    UpgradeDCUCommandResponse,
    UpgradeDCUFWCommandPayload
  >({
    query: (data) => ({
      url: `trigger-upgrade`,
      method: 'POST',
      body: data
    })
  }),
  uploadFirmwareFile: builder.mutation<
    UploadFirmwareFileResponse,
    UploadFirmwareFilePayload
  >({
    queryFn: async ({ formData, onProgress, signal }) => {
      const baseUrl = `${import.meta.env.VITE_HES_BASE_URL}/${
        import.meta.env.VITE_HES_API_VERSION
      }/command-execution/upload-bin-file-to-s3`;

      return new Promise((resolve, reject) => {
        // Initialize XMLHttpRequest
        const xhr = new XMLHttpRequest();

        // Open the request
        xhr.open('POST', baseUrl);

        // Set Authorization header if token exists
        const token = localStorage.getItem('hes_token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        // Monitor the upload progress
        xhr.upload.onprogress = function (e: ProgressEvent<EventTarget>) {
          const percentComplete = Math.ceil((e.loaded / e.total) * 100);
          onProgress(percentComplete); // Update progress
        };

        // Handle successful response
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ data: JSON.parse(xhr.responseText) });
          } else {
            reject(new Error(xhr.responseText || 'Unknown upload error'));
          }
        };

        // Handle errors during the request
        xhr.onerror = () => {
          reject(new Error(xhr.responseText || 'Unknown upload error'));
        };

        // Listen for request cancellation via AbortSignal
        signal.addEventListener('abort', () => {
          xhr.abort(); // Abort the XMLHttpRequest
          reject({ error: { status: xhr.status, data: 'Upload aborted!' } });
        });

        // Send the formData to the server
        xhr.send(formData);
      });
    }
  })
});
